import { useQuery } from "@tanstack/react-query";
import { dashApi } from "@/lib/dashApi";
import { fmtValue } from "@/lib/format";
import { EmptyState, ErrorState, LoadingState, PageHeader, Panel } from "@/components/dash/ui";

export default function ForecastAccuracyPage() {
  const q = useQuery({ queryKey: ["forecast-accuracy"], queryFn: dashApi.forecastAccuracy });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Forecast Accuracy"
        description="How well past forecasts predicted what actually happened. MAPE is the mean absolute % error; bias is the mean signed % error (positive = we over-forecast)."
      />
      {q.isLoading ? <LoadingState /> : q.isError ? <ErrorState error={q.error} /> : !q.data?.have_data ? (
        <Panel title="Accuracy"><EmptyState>No matured forecasts yet. Accuracy accrues as each day's forecast reaches its target date — check back after a few days of history.</EmptyState></Panel>
      ) : (
        <>
          <Panel title="Accuracy by KPI">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wide text-slate-500">
                  <th className="pb-2 font-medium">KPI</th>
                  <th className="pb-2 text-right font-medium">Evaluated</th>
                  <th className="pb-2 text-right font-medium">MAPE</th>
                  <th className="pb-2 text-right font-medium">Bias</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-500/40">
                {q.data.kpis.map((k) => (
                  <tr key={k.kpi} className="text-slate-300">
                    <td className="py-2 text-slate-100">{k.label}</td>
                    <td className="py-2 text-right tabular-nums">{k.evaluated}</td>
                    <td className="py-2 text-right tabular-nums text-white">{k.mape === null ? "—" : `${k.mape}%`}</td>
                    <td className="py-2 text-right tabular-nums text-slate-400">{k.bias_pct === null ? "—" : `${k.bias_pct > 0 ? "+" : ""}${k.bias_pct}%`}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Panel>
          {q.data.kpis.map((k) => (
            <Panel key={k.kpi} title={`${k.label} — recent forecasts vs actual`}>
              {!k.recent.length ? <EmptyState compact>No matured forecasts.</EmptyState> : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[11px] uppercase tracking-wide text-slate-500">
                      <th className="pb-2 font-medium">Made on</th>
                      <th className="pb-2 font-medium">For window ending</th>
                      <th className="pb-2 text-right font-medium">Projected</th>
                      <th className="pb-2 text-right font-medium">Actual</th>
                      <th className="pb-2 text-right font-medium">Error</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-ink-500/40">
                    {k.recent.map((r, i) => (
                      <tr key={i} className="text-slate-300">
                        <td className="py-2 text-[11px] text-slate-500">{r.as_of}</td>
                        <td className="py-2 text-[11px] text-slate-500">{r.target_end}</td>
                        <td className="py-2 text-right tabular-nums text-slate-200">{fmtValue(r.projected, k.unit)}</td>
                        <td className="py-2 text-right tabular-nums text-white">{r.actual === null ? "—" : fmtValue(r.actual, k.unit)}</td>
                        <td className="py-2 text-right tabular-nums text-slate-400">{r.pct_error === null ? "—" : `${r.pct_error > 0 ? "+" : ""}${r.pct_error}%`}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Panel>
          ))}
          <p className="text-xs text-slate-600">{q.data.note}</p>
        </>
      )}
    </div>
  );
}
